import { Provider } from '@angular/core';
import { CurrencyRateDomainService } from '@app/domain/services';
import {
  GetCurrencyRatesUseCase,
  CreateCurrencyRateUseCase,
  UpdateCurrencyRateUseCase,
  DeleteCurrencyRateUseCase,
  GetLatestRateUseCase,
  FindNearestRateUseCase,
} from '@app/domain/use-cases';
import type { ICurrencyRateRepository, ICurrencyRepository } from '@app/domain/ports';
import { RATE_REPOSITORY, CURRENCY_REPOSITORY } from '../../tokens/infrastructure.tokens';

/**
 * Provides Currency Rate domain services and use cases.
 *
 * Includes:
 * - CurrencyRateDomainService: Business rules and validation
 * - 6 Use Cases: Get, GetLatest, FindNearest, Create, Update, Delete
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideCurrencyRateDomain(): Provider[] {
  return [
    // Domain Service
    {
      provide: CurrencyRateDomainService,
      useFactory: () => new CurrencyRateDomainService(),
      deps: [],
    },

    // Use Cases - Query
    {
      provide: GetCurrencyRatesUseCase,
      useFactory: (CurrencyRateRepository: ICurrencyRateRepository) =>
        new GetCurrencyRatesUseCase(CurrencyRateRepository),
      deps: [RATE_REPOSITORY],
    },
    {
      provide: GetLatestRateUseCase,
      useFactory: (CurrencyRateRepository: ICurrencyRateRepository) => new GetLatestRateUseCase(CurrencyRateRepository),
      deps: [RATE_REPOSITORY],
    },
    {
      provide: FindNearestRateUseCase,
      useFactory: (CurrencyRateRepository: ICurrencyRateRepository, domainService: CurrencyRateDomainService) =>
        new FindNearestRateUseCase(CurrencyRateRepository, domainService),
      deps: [RATE_REPOSITORY, CurrencyRateDomainService],
    },

    // Use Cases - Commands
    {
      provide: CreateCurrencyRateUseCase,
      useFactory: (
        CurrencyRateRepository: ICurrencyRateRepository,
        currencyRepository: ICurrencyRepository,
        domainService: CurrencyRateDomainService,
      ) => new CreateCurrencyRateUseCase(CurrencyRateRepository, currencyRepository, domainService),
      deps: [RATE_REPOSITORY, CURRENCY_REPOSITORY, CurrencyRateDomainService],
    },
    {
      provide: UpdateCurrencyRateUseCase,
      useFactory: (CurrencyRateRepository: ICurrencyRateRepository, domainService: CurrencyRateDomainService) =>
        new UpdateCurrencyRateUseCase(CurrencyRateRepository, domainService),
      deps: [RATE_REPOSITORY, CurrencyRateDomainService],
    },
    {
      provide: DeleteCurrencyRateUseCase,
      useFactory: (CurrencyRateRepository: ICurrencyRateRepository) =>
        new DeleteCurrencyRateUseCase(CurrencyRateRepository),
      deps: [RATE_REPOSITORY],
    },
  ];
}
